import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Monitor, Smartphone } from 'lucide-react';
import { CourseMediaItem, Lesson } from '../../types';
import { CourseBasics } from './types';
import LivePreview from './LivePreview';

interface PreviewModalProps {
  basics: CourseBasics;
  gallery: CourseMediaItem[];
  lessons: Lesson[];
  onClose: () => void;
}

type Device = 'desktop' | 'mobile';

// Centered pop-up preview of the public course page, with a Desktop / Mobile view switch.
// Portaled to <body> with z-[60] so it covers the fixed site header (z-50) and isn't trapped
// in any page stacking context.
export default function PreviewModal({ basics, gallery, lessons, onClose }: PreviewModalProps) {
  const { t } = useTranslation('teaching');
  const [device, setDevice] = useState<Device>('desktop');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = 'unset'; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const toggle = (key: Device, Icon: typeof Monitor, label: string) => (
    <button
      type="button"
      onClick={() => setDevice(key)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        device === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <Icon className="w-4 h-4" />{label}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-6xl h-[92vh] sm:h-[88vh] bg-cream rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <span className="hidden md:block text-xs font-medium text-gray-400 uppercase tracking-wide">
            {t('courseEdit.livePreview', { defaultValue: 'Live preview' })}
          </span>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {toggle('desktop', Monitor, t('courseEdit.previewDesktop', { defaultValue: 'Desktop' }))}
            {toggle('mobile', Smartphone, t('courseEdit.previewMobile', { defaultValue: 'Mobile' }))}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-200"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-gray-100 p-3 sm:p-6">
          {device === 'mobile' ? (
            <div className="mx-auto w-[390px] max-w-full bg-white rounded-[2.2rem] border-[10px] border-gray-900 overflow-hidden shadow-2xl">
              <LivePreview basics={basics} gallery={gallery} lessons={lessons} mode="mobile" />
            </div>
          ) : (
            // Keep a true desktop layout; scroll horizontally on screens narrower than the frame.
            <div className="mx-auto w-full min-w-[920px] max-w-5xl bg-white rounded-xl shadow overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
                <span className="w-3 h-3 rounded-full bg-red-400" />
                <span className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <LivePreview basics={basics} gallery={gallery} lessons={lessons} mode="desktop" />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
